import type {
  IPayout,
  IPayout2,
  IPayoutDetails,
  IPayoutDetails2,
} from "@gen/app-query";

import { downloadCsv } from "~/utilities/csv";
import { renderMoneyWithoutCurrencySymbol } from "~/utilities/money";

import { getPayoutRefundAmount, isAdminPayout } from "./payoutData";

const CSV_HEADERS = [
  "Date",
  "Payout ID",
  "Type",
  "ID",
  "Client Name",
  "Currency",
  "Amount",
  "Description",
];

const PayoutCsvRowType = {
  Transaction: "Transaction",
  Refund: "Refund",
} as const;

function formatExportDate(locale: string, date: Date, timeZone: string) {
  return date.toLocaleDateString(locale, {
    timeZone,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export interface PayoutExportOptions {
  payoutDetails: IPayoutDetails | IPayoutDetails2;
  locale: string;
  timeZone: string;
}

function getPayoutId(payout: IPayout | IPayout2) {
  return isAdminPayout(payout)
    ? payout.payout_summary.payout_id
    : payout.payout_id;
}

export function buildPayoutCsvRows({
  payoutDetails,
  locale,
  timeZone,
}: PayoutExportOptions): unknown[][] {
  const payoutId = getPayoutId(payoutDetails.payout) || "N/A";

  return [
    CSV_HEADERS,
    ...payoutDetails.transactions.map((transaction) => [
      formatExportDate(locale, transaction.funds_released, timeZone),
      payoutId,
      PayoutCsvRowType.Transaction,
      transaction.v2_id ?? transaction.id,
      transaction.client_name === "" ? "N/A" : transaction.client_name,
      transaction.currency,
      renderMoneyWithoutCurrencySymbol(
        transaction.amount_released,
        transaction.currency,
        locale,
      ),
      transaction.description,
    ]),
    // Refunds are exported as negative amounts so that the rows of a payout
    // add up to its total.
    ...payoutDetails.refunds.map((payoutRefund) => [
      formatExportDate(locale, payoutRefund.refund.refunded, timeZone),
      payoutId,
      PayoutCsvRowType.Refund,
      payoutRefund.refund.id,
      payoutRefund.client_name === "" ? "N/A" : payoutRefund.client_name,
      payoutRefund.currency,
      renderMoneyWithoutCurrencySymbol(
        getPayoutRefundAmount(payoutRefund),
        payoutRefund.currency,
        locale,
      ),
      `Refund of transaction ${payoutRefund.v2_id ?? payoutRefund.transaction_id}`,
    ]),
  ];
}

export function downloadPayoutCsv(options: PayoutExportOptions) {
  const payoutId = getPayoutId(options.payoutDetails.payout);
  downloadCsv(buildPayoutCsvRows(options), `${payoutId ?? "export"}.csv`);
}
